import React, { useEffect, useRef } from "react";
import styles from "./NumberInputWithArrows.module.css";

export interface NumberInputWithArrowsProps {
  /** 当前值 */
  value: string | number;
  /** 值改变回调 */
  onChange: (value: string) => void;
  /** 最小值 */
  min?: number;
  /** 最大值 */
  max?: number;
  /** 单位文本 */
  unit?: string;
  /** 是否禁用 */
  disabled?: boolean;
  /** 自定义增量步长，默认为1 */
  step?: number;
  /** 自定义类名 */
  className?: string;
  /** 是否支持小数，默认 false */
  decimal?: boolean;
  /** 是否显示步进箭头，默认 true */
  showArrows?: boolean;
  /** 输入框聚焦回调 */
  onFocus?: React.FocusEventHandler<HTMLInputElement>;
  /** 输入框失焦回调 */
  onBlur?: React.FocusEventHandler<HTMLInputElement>;
  /** 自动聚焦 */
  autoFocus?: boolean;
}

const formatDecimal = (v: number, step: number): string => {
  const precision = Math.max(0, Math.ceil(-Math.log10(step)));
  return v.toFixed(precision);
};

export const NumberInputWithArrows: React.FC<NumberInputWithArrowsProps> = ({
  value,
  onChange,
  min = 1,
  max = 9999,
  unit = "",
  disabled = false,
  step = 1,
  className = "",
  decimal = false,
  showArrows = true,
  onFocus,
  onBlur,
  autoFocus = false,
}) => {
  const inputRef = useRef<HTMLInputElement>(null);
  const inputStr = value === undefined || value === null ? "" : String(value);
  const inputCh = Math.max(3, inputStr.length);

  useEffect(() => {
    if (autoFocus) {
      inputRef.current?.focus();
    }
  }, [autoFocus]);

  const increment = () => {
    const currentValue = Number(value);
    const stepped = Math.round((currentValue + step) / step) * step;
    const newValue = Math.min(stepped, max);
    onChange(decimal ? formatDecimal(newValue, step) : String(newValue));
  };

  const decrement = () => {
    const currentValue = Number(value);
    const stepped = Math.round((currentValue - step) / step) * step;
    const newValue = Math.max(stepped, min);
    onChange(decimal ? formatDecimal(newValue, step) : String(newValue));
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (decimal) {
      const sanitized = e.target.value.replace(/[^\d.]/g, "");
      const parts = sanitized.split(".");
      const clean =
        parts[0] + (parts.length > 1 ? "." + parts.slice(1).join("") : "");
      if (clean === "" || clean === ".") {
        onChange(clean);
        return;
      }
      const numValue = Number(clean);
      if (!isNaN(numValue)) {
        onChange(clean);
      }
    } else {
      const digitsOnly = e.target.value.replace(/\D/g, "");
      if (digitsOnly === "") {
        onChange("");
        return;
      }
      const numValue = Number(digitsOnly);
      if (!isNaN(numValue)) {
        const clampedValue = Math.max(min, Math.min(max, numValue));
        onChange(String(clampedValue));
      }
    }
  };

  const handleBlur = () => {
    const numValue = Number(value);

    if (value === "" || isNaN(numValue)) {
      onChange(decimal ? formatDecimal(min, step) : String(min));
      return;
    }

    if (decimal) {
      const stepped = Math.round(numValue / step) * step;
      const clamped = Math.max(min, Math.min(max, stepped));
      onChange(formatDecimal(clamped, step));
      return;
    }

    // 限制在范围内
    if (numValue < min) {
      onChange(String(min));
    } else if (numValue > max) {
      onChange(String(max));
    }
  };

  return (
    <div
      className={`${styles.inputWithArrows} ${className}`}
      onClick={(e) => {
        e.stopPropagation();
        inputRef.current?.focus();
      }}
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div className={styles.inputContainer}>
        <input
          ref={inputRef}
          type="text"
          inputMode="numeric"
          autoComplete="off"
          spellCheck={false}
          className={styles.configInput}
          value={value}
          style={{ width: `${inputCh}ch`, minWidth: "40px" }}
          onChange={(e) => {
            e.stopPropagation(); // 阻止事件冒泡
            handleChange(e);
          }}
          onBlur={(e) => {
            e.stopPropagation();
            handleBlur();
            onBlur?.(e);
          }}
          onClick={(e) => e.stopPropagation()}
          onFocus={(e) => {
            e.stopPropagation();
            onFocus?.(e);
          }}
          disabled={disabled}
        />
        {unit && <span className={styles.inputUnit}>{unit}</span>}
      </div>
      {showArrows && (
        <div className={styles.arrowButtons}>
          <button
            className={styles.arrowBtn}
            onClick={(e) => {
              e.stopPropagation();
              increment();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={disabled}
            type="button"
            aria-label="增加"
          >
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
              <path
                d="M1 7L6 2L11 7"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="square"
                strokeLinejoin="miter"
              />
            </svg>
          </button>
          <button
            className={styles.arrowBtn}
            onClick={(e) => {
              e.stopPropagation();
              decrement();
            }}
            onMouseDown={(e) => e.stopPropagation()}
            disabled={disabled}
            type="button"
            aria-label="减少"
          >
            <svg width="12" height="8" viewBox="0 0 12 8" fill="none">
              <path
                d="M1 1L6 6L11 1"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="square"
                strokeLinejoin="miter"
              />
            </svg>
          </button>
        </div>
      )}
    </div>
  );
};
